import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, forkJoin } from 'rxjs';
import { Scan } from 'src/app/models/scan.model';
import { User } from 'src/app/models/user.model';
import { ScanService } from 'src/app/services/scan.service';
import { Analysis, Patient, SensingtexService } from 'src/app/services/sensingtex.service';
import { UserService } from 'src/app/services/user.service';
import Swal from 'sweetalert2';
import { invertFootData } from '../analysis-details/pressureMapHelpers';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { AnalysisComponent } from '../analysis/analysis.component';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';

@Component({
  selector: 'app-analysis-details-report-pdf',
  standalone: true,
  imports: [SharedModule, AnalysisComponent],
  templateUrl: './analysis-details-report-pdf.component.html',
  styleUrl: './analysis-details-report-pdf.component.scss'
})
export class AnalysisDetailsReportPdfComponent {
  @ViewChild('analysisComponent') analysisComponent: AnalysisComponent;
  @Input() showActions: boolean = true
  @Output() PDFGenerationComplete = new EventEmitter<void>()

  public user: User;
  orderId: string;
  order: Scan = null;
  analysis: Analysis = null;
  analysisId: string
  isLoading: boolean = false
  generatingPDF: boolean = false


  patients: Patient[] = []
  patientAnalyses: Analysis[] = []
  selectedPatient: Patient
  visible: boolean = false

  constructor(
    private route: ActivatedRoute,
    private orderService: ScanService,
    private sensingtexService: SensingtexService,
    private userService: UserService,
    private router: Router
  ) {

    // Get data from the URL path
    this.route.params.subscribe(params => {
      this.orderId = params['orderId'];
    });

    // Scroll to top on click some analysis
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.scrollToTop();
    });
  }

  ngOnInit(): void {
    this.user = this.userService.user
  }

  ngAfterViewInit(): void {
    this.loadRequiredData();
  }


  loadRequiredData(analysisId?: string) {
    // Load all the patients only when no patient selected
    if (!this.selectedPatient) {
      // Load sensingtex Patients
      this.loadPatients()
    } else {

      if (this.patientAnalyses && this.patientAnalyses.length > 0) {
        this.analysis = null;
        setTimeout(() => {
          this.analysis = this.patientAnalyses.find(a => a.id === analysisId);
        }, 1);
      }
    }
  }

  loadPatients() {
    this.isLoading = true

    forkJoin([
      this.orderService.getOne(this.orderId),
      this.sensingtexService.getPatients(this.user.sensingtexManagerId)
    ]).subscribe(([scan, patients]) => {
      this.order = scan;
      this.patients = patients;

      const matchingPatient = patients.find(patient => patient.id === this.order?.sensingtexPatientId);

      if (matchingPatient) {
        this.selectedPatient = matchingPatient;
        this.loadPatientAnalysis(matchingPatient.id);
      } else {
        this.selectedPatient = null;
      }
    });
  }

  loadPatientAnalysis(patientId: string) {
    this.sensingtexService.getAllAnalysesByPatientId(patientId, this.user.sensingtexManagerId, this.user.sensingtexHardwareId).subscribe(patientAnalyses => {
      this.patientAnalyses = patientAnalyses.map(patientAnalysis => {

        if (patientAnalysis.type === "Dynamic") {
          patientAnalysis.additionalParameters[12]["activePointsLeft"] = invertFootData(patientAnalysis.additionalParameters[12]["activePointsLeft"], { x: false, y: false });
          patientAnalysis.additionalParameters[13]["activePointsRight"] = invertFootData(patientAnalysis.additionalParameters[13]["activePointsRight"], { x: true, y: true });
        }

        return {
          ...patientAnalysis,
          creationDate: this.formatCreationDate(patientAnalysis.creationDate)
        }
      })
      this.isLoading = false
      this.generateAnalysisDetailsPDF();

    })
  }

  formatCreationDate(rawDate: string): string {
    const date = new Date(rawDate);

    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    const formattedDate = `${month} ${day} ${year} ${hours}:${minutes}:${seconds}`;

    return formattedDate;
  }

  async generateAnalysisDetailsPDF() {
    this.generatingPDF = true;

    Swal.fire({
      title: 'Generating PDF',
      text: 'Please wait a few seconds...',
      icon: 'info',
      showConfirmButton: false,
      allowOutsideClick: false,
    });

    const pdf = new jsPDF('p', 'pt', 'a4');
    const patientName = this.order.data['Patient Data']['name'];
    const patientSurname = this.order.data['Patient Data']['surname'];
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });

    // Poner en primer lugar el "Static"
    this.patientAnalyses.sort((analysisA, analysisB) => {
      if (analysisA.type === "Dynamic") {
        return 1
      } else {
        return -1
      }
    })

    setTimeout(async () => {
      for (let i = 0; i < this.patientAnalyses.length; i++) {
        const elementId = `analysis-details-pdf-${i}`;
        const elementToPrint = document.getElementById(elementId);

        if (i === 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.text('Patient name: ', 20, 30);
          pdf.setFont('helvetica', '');
          pdf.text(`${patientName} ${patientSurname}`, 89, 30);
          pdf.setFont('helvetica', 'bold');
          pdf.text('PDF creation date: ', 20, 50);
          pdf.setFont('helvetica', '');
          pdf.text(currentDate, 112, 50);
        }

        if (!elementToPrint) {
          console.error(`Element with ID ${elementId} not found`);
          continue;
        }

        let reportDivWidth = elementToPrint.clientWidth;
        let reportDivHeight = elementToPrint.clientHeight;

        await html2canvas(elementToPrint).then((canvas) => {
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          if (this.patientAnalyses[i].type === 'Balance') {
            const canvasHeight60 = Math.floor(canvas.height * 0.80);
            const canvasHeight40 = canvas.height - canvasHeight60;

            const top60Canvas = document.createElement('canvas');
            const bottom40Canvas = document.createElement('canvas');

            top60Canvas.width = canvas.width;
            top60Canvas.height = canvasHeight60;
            bottom40Canvas.width = canvas.width;
            bottom40Canvas.height = canvasHeight40;

            const top60Context = top60Canvas.getContext('2d');
            const bottom40Context = bottom40Canvas.getContext('2d');

            top60Context.drawImage(canvas, 0, 0, canvas.width, canvasHeight60, 0, 0, canvas.width, canvasHeight60);
            bottom40Context.drawImage(canvas, 0, canvasHeight60, canvas.width, canvasHeight40, 0, 0, canvas.width, canvasHeight40);

            const imageWidth = pdfWidth;
            const imageHeight90 = pdfHeight * 0.9;
            const imageHeight30 = pdfHeight * 0.3;

            const x = 0;
            const y = 0;

            pdf.addImage(top60Canvas.toDataURL('image/jpeg'), 'JPEG', x, y, imageWidth, imageHeight90, null, 'FAST');
            pdf.addPage();
            pdf.addImage(bottom40Canvas.toDataURL('image/jpeg'), 'JPEG', x, y, imageWidth, imageHeight30, null, 'FAST');
          } else {
            let aspectRatioWidth = pdfWidth / reportDivWidth;
            let aspectRatioHeight = pdfHeight / reportDivHeight;
            let minFactor = Math.min(aspectRatioWidth, aspectRatioHeight);

            let imageWidth = reportDivWidth * minFactor;
            let imageHeight = reportDivHeight * minFactor;

            const x = (pdfWidth - imageWidth) / 2;
            const y = (pdfHeight - imageHeight) / 2;

            pdf.addImage(canvas.toDataURL('image/jpeg'), 'JPEG', x, y, imageWidth, imageHeight, null, 'FAST');
          }

          // If is not latest page, finish actual page
          if (i < this.patientAnalyses.length - 1) {
            pdf.addPage();
          }

          // If it's the latest page, save the PDF 
          if (i === this.patientAnalyses.length - 1) {
            pdf.save(`${patientName}_${patientSurname}_Report.pdf`, {
              returnPromise: true
            }).then(() => {
              this.generatingPDF = false;
              this.PDFGenerationComplete.emit();

              Swal.close();
              Swal.fire({
                title: 'PDF Generated',
                text: 'The PDF has been generated successfully.',
                icon: 'success',
                timer: 3000,
                showConfirmButton: true
              });
            })
          }

        }).catch(error => {
          console.error(`Error generating canvas for element with ID ${elementId}:`, error);
        });
      }
    }, 1);
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }
}
